import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { batchAddMembersToCompanyFn } from 'store/companies/companiesSlice';
import { exactWhite, lightGray, stringToColour } from 'utils/style/colors';
import messages from './Messages';
import Icon from 'components/Icon';
import Input from 'components/Input';
import {
  Badge,
  Option,
  // ItemList,
  OptionsBox,
  // EmptyOption,
  AddMemberWrapper,
  SearchAndAddBar,
  RolesWrapper,
} from './styles';
import TaskUserList from './TaskUserList';
import APopover from '../APopover';
import Button from '../Button/Button';
import notify from '../Notify';
import { englishNumber } from 'utils/number';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import { IAssignmentHistory, ITask, TaskStatus } from 'store/tasks/tasksType';
import { IInvitedMember } from 'store/companies/companiesTypes';
import { IPermission } from 'store/entity/IPermission';
import i18n, { languageDirection } from '../../i18n';

interface ISelectorOption {
  name: string;
  optionType: 'role' | 'user' | 'projectMember';
}

export interface IUserOption extends ISelectorOption {
  roles: { name: string; id: number }[];
  status?: TaskStatus;
  userId: number;
  progress?: number;
  optionType: 'user';
  progressDescription?: string;
  inactive?: boolean;
  relatedRoleId?: number;
  relatedRoleName?: string;
  assignmentId?: number;
}

export interface IRoleOption extends ISelectorOption {
  name: string;
  roleId: number;
  optionType: 'role';
}

export interface IProjectMemberOption extends ISelectorOption {
  optionType: 'projectMember';
}

interface IComponentProps {
  label: string;
  values: Array<IUserOption | IRoleOption>;
  task?: ITask;
  active?: boolean;
  usePop?: boolean;
  disable?: boolean;
  compact?: boolean;
  options: Array<IUserOption | IRoleOption>;
  type: string;
  projectName?: string;
  singleChoice?: boolean;
  projectMembers?: number[];
  canChangeProgress: boolean;
  canChangeSelfProgress?: boolean;
  permission?: IPermission;
  thisUserId?: number;
  onChangeValues: (values: Array<IUserOption | IRoleOption>) => void;
  onChangeProgress?: (
    assigneeId: number,
    progress: string,
    status: TaskStatus,
    progressDescription: string
  ) => void;
  addMultipleMembersToCompany?: (
    invitedMembers: IInvitedMember[],
    forceRename: boolean,
    companyId: number
  ) => ReturnType<typeof batchAddMembersToCompanyFn>;
  allowAdd?: boolean;
  companyId?: number;
  emphasizeSelect?: string;
  largeScreen?: boolean;
  maxWidth?: string;
  hasOuterBorder?: boolean;
  onSetAssignmentHistory?: (assingmentHistory: IAssignmentHistory[]) => void;
  assigneeIdToShow?: number | null;
  onSetAssigneeIdToShow?: (asId: number | null) => void;
}

interface IComponentStates {
  task?: ITask;
  values: Array<IUserOption | IRoleOption>;
  isActive: boolean;
  inputValue: string;
  projectMembers: boolean;
  addViewOpen: boolean;
  addLoading: boolean;
  addUserName?: string;
  addUserPhone?: string;
}

class UserSelector extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state: IComponentStates = {
    isActive: false,
    inputValue: '',
    projectMembers: false,
    values: this.props.values,
    addLoading: false,
    addViewOpen: false,
  };

  inputRef = React.createRef<HTMLInputElement>();
  wrapperRef = React.createRef<HTMLDivElement>();
  addPopRef = React.createRef<HTMLDivElement>();

  static getDerivedStateFromProps(
    nextProps: IComponentProps,
    prevState: IComponentStates
  ) {
    const selectedUserIDs = nextProps.values
      .map((value) => {
        return (value as any).userId;
      })
      .filter((it) => it !== null && it !== undefined);
    const projectMembers =
      nextProps.projectMembers &&
      !nextProps.projectMembers.find((pm) => {
        return selectedUserIDs.find((s) => pm === s) === undefined;
      });
    if (nextProps.task !== prevState.task) {
      return {
        task: nextProps.task,
        values: nextProps.values,
        projectMembers: projectMembers,
      };
    }
    return {};
  }
  componentDidUpdate(): void {
    this.setState({
      values: this.props.values,
    });
  }

  onFocus = (event: any) => {
    event.stopPropagation();
    this.setState({ isActive: !this.state.isActive });
    if (
      this.inputRef.current &&
      (!this.addPopRef.current ||
        !this.addPopRef.current.contains(event.target))
    ) {
      this.inputRef.current.focus();
    }
  };

  handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const value = e.target.value;
    this.setState({ inputValue: value });
  };

  handleChooseAnItem =
    (
      option?: IUserOption | IRoleOption,
      relatedRoleId?: number,
      relatedRoleName?: string,
      manual?: boolean
    ) =>
    (e?: React.ChangeEvent<HTMLInputElement>) => {
      e?.stopPropagation();
      const value = manual || e?.target.checked || !!relatedRoleId;
      if (value && this.props.singleChoice) this.setState({ isActive: false });
      this.inputRef.current?.select();

      if (option) {
        if (this.props.singleChoice && this.state.values.length > 0 && value) {
          const finalOption = {
            ...option,
            relatedRoleId: relatedRoleId,
            relatedRoleName: relatedRoleName,
          } as any;
          this.setState({ values: [finalOption] });
          this.handleConfirmChanges([finalOption]);
        } else {
          if (option.optionType === 'role') {
            const roleOption = option as IRoleOption;
            // a role checked or unchecked
            let values = cloneDeep(this.state.values);
            if (value) {
              values = [...values, roleOption];
            } else {
              values = values.filter(
                (v) =>
                  (v.optionType === 'user' &&
                    !v.roles.includes({
                      name: roleOption.name,
                      id: roleOption.roleId,
                    })) ||
                  (v.optionType === 'role' && v.roleId !== roleOption.roleId)
              );
            }
            this.props.options.forEach((item) => {
              if (
                item.optionType === 'user' &&
                item.roles.some((e) => e.id === option.roleId)
              ) {
                if (value) {
                  if (
                    !values.find(
                      (v) => (v as IUserOption).userId === item.userId
                    )
                  ) {
                    values = [
                      ...values,
                      {
                        ...item,
                        relatedRoleId: option.roleId,
                        relatedRoleName: option.name,
                      },
                    ];
                  }
                } else {
                  values = values.filter(
                    (v) =>
                      v.optionType === 'role' ||
                      (v.optionType === 'user' &&
                        !v.roles.includes({
                          name: option.name,
                          id: option.roleId,
                        }))
                  );
                }
              }
            });
            this.setState({ values });
            this.handleConfirmChanges(values);
          } else {
            let values = cloneDeep(this.state.values) as Array<
              IUserOption | IRoleOption
            >;
            if (value) {
              const currentIndex = values.findIndex(
                (e) => e.optionType === 'user' && e.userId === option.userId
              );
              if (currentIndex >= 0) {
                values.splice(currentIndex, 1, {
                  ...values[currentIndex],
                  relatedRoleId: relatedRoleId,
                  relatedRoleName: relatedRoleName,
                } as IUserOption);
              } else
                values.push({
                  ...option,
                  relatedRoleId: relatedRoleId,
                  relatedRoleName: relatedRoleName,
                });
            } else {
              values = values.filter(
                (item) =>
                  item.optionType === 'user' && item.userId !== option.userId
              );
            }
            this.setState({ values });
            this.handleConfirmChanges(values);
          }
        }
      } else {
        const members: IUserOption[] = [];
        (this.props.projectMembers as number[]).forEach((userId) => {
          const userOption = this.props.options.find(
            (opt) => opt.optionType === 'user' && opt.userId === userId
          );
          if (userOption) {
            members.push(userOption as IUserOption);
          }
        });
        let values: Array<IUserOption | IRoleOption> = [];
        const oldValues = cloneDeep(this.state.values);
        if (value) {
          const filteredNewValues = members.filter(
            (member) =>
              !oldValues.find(
                (val) =>
                  val.optionType === 'user' && val.userId === member.userId
              )
          );
          values = [...oldValues, ...filteredNewValues];
        } else {
          values = oldValues.filter((val) => {
            if (val.optionType === 'role') {
              return true;
            }
            return !members.find((v) => v.userId === val.userId);
          });
        }

        this.setState({ values, projectMembers: value });
        this.handleConfirmChanges(values);
      }
    };

  handleConfirmChanges = (valuesState: Array<IUserOption | IRoleOption>) => {
    const values = valuesState.filter((value) => value.optionType === 'user');
    this.props.onChangeValues(values);
  };

  handleAddUser = () => {
    const { addUserName, addUserPhone } = this.state;
    if (addUserPhone?.length !== 11) {
      notify(i18n.t('pleaseFillCorrectPhone', { ns: 'general' }), 'warning');
      return;
    }
    if (!addUserName?.trim()) {
      notify(i18n.t('pleaseFillUserName', { ns: 'general' }), 'warning');
      return;
    }
    if (this.props.companyId && this.props.addMultipleMembersToCompany) {
      this.setState({ addLoading: true });
      this.props
        .addMultipleMembersToCompany(
          [
            {
              name: addUserName,
              phoneNumber: englishNumber(addUserPhone),
              roles: [],
              inactive: true,
              toDeleteRoleIds: [],
              employeeId: '',
            },
          ],
          false,
          this.props.companyId
        )
        .then(
          (data) => {
            this.setState({
              addLoading: false,
              addUserName: undefined,
              addUserPhone: undefined,
              addViewOpen: false,
            });
            const createdUser = data.users[0];
            this.handleChooseAnItem(
              {
                userId: createdUser.id,
                name: createdUser.name,
                status: 'notStarted',
                inactive: true,
                roles: [],
                optionType: 'user',
              },
              undefined,
              undefined,
              true
            )();
            notify(messages.memberAdded(createdUser?.name));
          },
          () => {
            this.setState({
              addLoading: false,
            });
            notify(i18n.t('errorAddingUser', { ns: 'general' }));
          }
        );
    }
  };

  onChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const phoneNumber = englishNumber(inputValue);
    if (!isNaN(Number(phoneNumber))) {
      this.setState({ addUserPhone: inputValue });
    }
  };
  render() {
    const {
      label,
      task,
      values,
      disable,
      options,
      projectName,
      projectMembers,
      onChangeProgress,
      canChangeProgress,
      canChangeSelfProgress,
      compact,
      type,
      active,
      usePop,
      allowAdd,
      largeScreen,
      maxWidth = '800px',
      hasOuterBorder = false,
      assigneeIdToShow,
      onSetAssigneeIdToShow,
    } = this.props;

    const { addViewOpen, addLoading, addUserName, addUserPhone } = this.state;
    const filteredOptions = !(this.state.isActive || active)
      ? []
      : options.filter((option) => {
          if (option.optionType === 'role') {
            const filteredUsers = this.props.options.filter((o) => {
              return (
                o.optionType === 'user' &&
                !!o.roles.find((r) => r.name === option.name)
              );
            }) as IUserOption[];
            if (!filteredUsers.length) return false;
          }
          return (
            option.name?.includes(this.state.inputValue) ||
            (option.optionType === 'user' &&
              option.roles.find((it) =>
                it.name.includes(this.state.inputValue)
              )) /*&&
        (!option['inactive'] ||
          values.find((it) => it['userId'] === option['userId']))*/
          );
        });
    let sortdItems: ('projectMembers' | IUserOption | IRoleOption)[] =
      filteredOptions.sort((a, b) => {
        if (
          a.optionType === 'user' &&
          !!this.state.values.find((value) => {
            if (value.optionType === 'user') {
              return value.userId === a.userId;
            }
            return false;
          })
        )
          return -1;
        if (
          b.optionType === 'user' &&
          !!this.state.values.find((value) => {
            if (value.optionType === 'user') {
              return value.userId === b.userId;
            }
            return false;
          })
        )
          return 1;

        if (a.optionType === 'user' && b.optionType === 'role') {
          return 1;
        } else if (a.optionType === 'role' && b.optionType === 'user') {
          return -1;
        }
        if (
          a.optionType === 'user' &&
          a.inactive &&
          b.optionType === 'user' &&
          !b.inactive
        )
          return 1;
        else if (
          a.optionType === 'user' &&
          !a.inactive &&
          b.optionType === 'user' &&
          b.inactive
        )
          return -1;
        return 0;
      });
    if (projectMembers && !this.state.inputValue) {
      sortdItems = ['projectMembers', ...sortdItems];
    }
    const content = (
      <>
        {options && (
          <OptionsBox
            focused={this.state.isActive || active}
            maxWidth={maxWidth}
            hasOuterBorder={hasOuterBorder}
          >
            <SearchAndAddBar>
              {addViewOpen ? (
                <div />
              ) : (
                <Input
                  wrapperStyle={{ flexGrow: '1' }}
                  id={label}
                  type="text"
                  value={this.state.inputValue}
                  onChange={this.handleChangeInput}
                  forwardRef={this.inputRef}
                  placeholder={i18n.t('search', { ns: 'general' }) as string}
                />
              )}
              {allowAdd && (
                <Button
                  style={{ margin: '0px 10px' }}
                  color={'black-outline'}
                  onClick={() => {
                    this.setState({
                      addViewOpen: !addViewOpen,
                    });
                  }}
                >
                  <Icon
                    type={addViewOpen ? 'users' : 'plus'}
                    color={exactWhite}
                  />
                  {addViewOpen
                    ? (i18n.t('showMembers', { ns: 'general' }) as string)
                    : (i18n.t('invite', { ns: 'general' }) as string)}
                </Button>
              )}
            </SearchAndAddBar>
            {addViewOpen ? (
              <AddMemberWrapper ref={this.addPopRef}>
                <Input
                  id="addUserName"
                  type="text"
                  value={addUserName}
                  label={i18n.t('name', { ns: 'general' }) as string}
                  onChange={(e) =>
                    this.setState({ addUserName: e.target.value })
                  }
                />
                <Input
                  id="addPhone"
                  type="tel"
                  maxLength={11}
                  minLength={11}
                  value={addUserPhone}
                  placeholder={i18n.t('phonePlaceHolder', { ns: 'general' })}
                  label={i18n.t('phone', { ns: 'general' }) as string}
                  onChange={this.onChangePhoneNumber}
                />
                <Button
                  color={'green'}
                  loading={addLoading}
                  onClick={() => this.handleAddUser()}
                >
                  <Icon type="plus" color={exactWhite} />{' '}
                  {i18n.t('addUser', { ns: 'general' }) as string}{' '}
                </Button>
              </AddMemberWrapper>
            ) : (
              <List
                width="100"
                height={400}
                itemData={{ sortdItems }}
                itemSize={40}
                itemCount={sortdItems.length}
                direction={languageDirection()}
              >
                {(childProps: ListChildComponentProps) => {
                  const item = this.getItem(
                    label,
                    projectName,
                    sortdItems[childProps.index]
                  );
                  return item ? (
                    <div style={childProps.style}>{item}</div>
                  ) : null;
                }}
              </List>
            )}
          </OptionsBox>
        )}
      </>
    );
    return (
      <>
        {!active && values && (
          <TaskUserList
            trigger={'click'}
            type={type}
            onChangeProgress={onChangeProgress}
            task={task}
            canChangeSelfProgress={canChangeSelfProgress}
            canChangeProgress={canChangeProgress}
            permission={this.props.permission}
            compact={compact}
            values={values}
            singleChoice={this.props.singleChoice}
            onFocus={(e: any) => {
              e.stopPropagation();
              if (!disable) {
                this.onFocus(e);
              }
            }}
            disable={disable}
            emphasizeSelect={this.props.emphasizeSelect}
            onSetAssignmentHistory={this.props.onSetAssignmentHistory}
            assigneeIdToShow={assigneeIdToShow}
            onSetAssigneeIdToShow={onSetAssigneeIdToShow}
          />
        )}
        {!disable && (
          <React.Fragment>
            {(active || this.state.isActive) &&
              (!usePop ? (
                <div
                  style={{
                    width: 'calc(min(800px , 80vw))',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    borderRadius: '0.5rem',
                  }}
                >
                  {content}{' '}
                </div>
              ) : (
                <>
                  <APopover
                    placement={'auto'}
                    arrow
                    curveBorder
                    target={
                      <div
                        style={{
                          width: 'calc(min(800px , 80vw)) !important;',
                        }}
                      />
                    }
                    trigger="click"
                    show={true}
                    onShowChange={(show) => {
                      this.setState({ isActive: show });
                    }}
                  >
                    <div
                      style={{
                        width: largeScreen ? 'calc(min(800px , 80vw))' : 'auto',
                      }}
                    >
                      {content}
                    </div>
                  </APopover>
                  {/*<div style={{ height: '400px' }} />*/}
                </>
              ))}
          </React.Fragment>
        )}
      </>
    );
  }

  private getItem(
    label: string,
    projectName: string | undefined,
    item: 'projectMembers' | IUserOption | IRoleOption
  ) {
    if (item === 'projectMembers')
      return (
        <Option choid>
          <Input
            id={`${label}-projectMember`}
            type="checkbox"
            label={messages.projectMember(projectName)}
            checked={this.state.projectMembers}
            onChange={this.handleChooseAnItem()}
            autoComplete="off"
            color={stringToColour(messages.projectMember(projectName))}
          />
        </Option>
      );
    else if (item.optionType === 'role') {
      const filteredValues = (
        this.state.values.filter(
          (v) => v.optionType === 'user'
        ) as IUserOption[]
      ).map((u) => u.userId);
      const filteredUsers = (
        this.props.options.filter((o) => {
          return (
            o.optionType === 'user' &&
            !!o.roles.find((r) => r.name === item.name)
          );
        }) as IUserOption[]
      ).map((u) => u.userId);
      const checked =
        !!this.state.values.find((v) => {
          if (v.optionType === 'role') {
            return v.roleId === item.roleId;
          }
          return false;
        }) ||
        (filteredUsers &&
          filteredValues &&
          filteredUsers.every((value) => filteredValues.includes(value)));
      if (!filteredUsers.length) {
        return null;
      }
      return (
        <Option key={`ROLE_${item.roleId}`}>
          <Input
            type="checkbox"
            id={`${item.roleId}-role-${label}`}
            label={item.name}
            color={stringToColour(item.name)}
            onChange={this.handleChooseAnItem(item)}
            autoComplete="off"
            checked={checked}
          />
        </Option>
      );
    } else if (item.optionType === 'user') {
      return (
        <Option key={`USER_${item.userId}`}>
          <Input
            wrapperStyle={{ flexShrink: 0 }}
            type="checkbox"
            id={`${item.userId}-user-${label}`}
            label={
              item.name + (item.inactive ? ` (${messages.inactiveUser})` : '')
            }
            labelStyle={{
              color: item.inactive ? lightGray : undefined,
            }}
            onChange={(e) => {
              e.stopPropagation();
              this.handleChooseAnItem(item)(e);
            }}
            checked={
              !!this.state.values.find((value) => {
                if (value.optionType === 'user') {
                  return value.userId === item.userId;
                }
                return false;
              })
            }
          />
          {item.optionType === 'user' && (
            <RolesWrapper>
              {item.roles.map((role: { name: string; id: number }) => (
                <Badge
                  key={role.name}
                  color={stringToColour(role.name)}
                  onClick={this.handleChooseAnItem(item, role.id, role.name)}
                >
                  {role.name}
                </Badge>
              ))}
            </RolesWrapper>
          )}
        </Option>
      );
    } else {
      return null;
    }
  }
}

export default UserSelector;
